import { useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { ToastData } from '~/hooks/useToast';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { withNectarTrackingParams } from '~/utils/nectarTracking';

import { type GeolocationChange, geolocationField } from '../_utils/geolocationField';
import { builtInFieldValues } from '../_utils/onboardingProfileFields';

const SAVE_ONBOARDING_IDENTITY_ACTION = '/community_onboarding_identity';

type ActionResponse = { toast?: ToastData };

export type SaveOnboardingIdentityVars = {
  firstName?: string;
  lastName?: string;
  headline?: string;
  location?: string;
  geolocation?: GeolocationChange;
  avatar?: File | null;
  completesOnboarding?: boolean;
};

const onboardingIdentityFormData = ({
  firstName,
  lastName,
  headline,
  location,
  geolocation,
  avatar,
  completesOnboarding,
}: SaveOnboardingIdentityVars): FormData => {
  const formData = new FormData();
  if (firstName !== undefined) formData.append('first_name', firstName);
  if (lastName !== undefined) formData.append('last_name', lastName);
  for (const [key, value] of Object.entries({
    ...builtInFieldValues({ headline, location }),
    ...geolocationField(geolocation),
  })) {
    formData.append(key, value);
  }
  if (completesOnboarding) {
    formData.append('completes_onboarding', 'true');
    withNectarTrackingParams(formData, NectarPageBlock.CommunityOnboardingWizard);
  }
  if (avatar instanceof File && avatar.size > 0) {
    formData.append('avatar', avatar);
  }

  return formData;
};

const submitOnboardingIdentity = async (formData: FormData, csrfToken: string): Promise<ActionResponse> => {
  const response = await fetch(SAVE_ONBOARDING_IDENTITY_ACTION, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: formData,
  });

  const data: unknown = await response.json().catch(() => null);
  const toast =
    typeof data === 'object' && data !== null && 'toast' in data && typeof data.toast === 'object'
      ? (data.toast as ToastData)
      : undefined;

  if (!response.ok) {
    throw new Error(toast?.message ?? 'Community action failed');
  }

  return { toast };
};

export const useSaveOnboardingIdentityMutation = () => {
  const ensureCsrfToken = useEnsureCsrfToken();

  return useMutation({
    mutationFn: async (vars: SaveOnboardingIdentityVars) =>
      submitOnboardingIdentity(onboardingIdentityFormData(vars), await ensureCsrfToken()),
  });
};
